import { useState } from "react";
import { Mail, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline email-capture for end-of-article placement.
 *
 * Props:
 *  - source: tracking tag like `learn_article:<slug>` so the lead row shows
 *    which surface converted. Defaults to "learn_article" (untagged).
 *
 * Behavior:
 *  - Two fields (first name optional, email required)
 *  - Calm validation, single-shot submit
 *  - Success state shows a confirmation, NOT a toast (toast feels too
 *    disposable for an opt-in)
 *  - Idempotent on the server side — repeat submits show "already on the list"
 */
export default function NewsletterInlineCapture({ source = "learn_article" }) {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null); // null | "subscribed" | "already"

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        const cleaned = email.trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleaned)) {
            toast.error("That doesn't look like a valid email.");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/newsletter/subscribe`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: cleaned,
                    first_name: firstName.trim() || null,
                    source,
                }),
            });
            if (!r.ok) {
                const body = await r.json().catch(() => ({}));
                throw new Error(body.detail || "Subscription failed");
            }
            const data = await r.json();
            setDone(data.already_subscribed ? "already" : "subscribed");
        } catch (err) {
            toast.error(err.message || "Couldn't subscribe — try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div
                className="mt-12 bl-card p-7 md:p-9 bg-bl-cream border-bl-line"
                data-testid="newsletter-capture-success"
            >
                <div className="flex items-start gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-bl-primary text-white flex items-center justify-center">
                        <Check className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display font-semibold text-xl text-bl">
                            {done === "already"
                                ? "You're already on the list."
                                : "You're in."}
                        </div>
                        <p className="mt-2 text-bl-muted leading-relaxed">
                            {done === "already"
                                ? "Watch for the next deep-dive Tuesday morning."
                                : "Check your inbox for a quick welcome — the next inspection deep-dive lands Tuesday."}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="mt-12 bl-card p-7 md:p-9 bg-bl-cream border-bl-line"
            data-testid="newsletter-inline-capture"
        >
            <div className="flex items-start gap-4 mb-5">
                <div className="shrink-0 w-10 h-10 rounded-full bg-bl-primary/10 text-bl-primary flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                </div>
                <div>
                    <div className="bl-eyebrow !text-bl-primary">From the editorial library</div>
                    <h3 className="mt-2 font-display font-semibold text-xl md:text-2xl text-bl leading-snug">
                        Get the next inspection deep-dive in your inbox.
                    </h3>
                    <p className="mt-2 text-bl-muted leading-relaxed text-sm md:text-base">
                        One calm, claymation-illustrated guide per week. No fearmongering, no spam — unsubscribe in one click.
                    </p>
                </div>
            </div>

            <div className="grid sm:grid-cols-[1fr_2fr_auto] gap-3">
                <input
                    type="text"
                    className="bl-input"
                    placeholder="First name (optional)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    disabled={busy}
                    data-testid="newsletter-first-name-input"
                />
                <input
                    type="email"
                    className="bl-input"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    disabled={busy}
                    data-testid="newsletter-email-input"
                />
                <button
                    type="submit"
                    disabled={busy}
                    className="bl-btn-primary text-sm gap-1.5 whitespace-nowrap"
                    data-testid="newsletter-submit-btn"
                >
                    {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <>
                            Subscribe
                            <ArrowRight className="w-4 h-4" />
                        </>
                    )}
                </button>
            </div>
            <p className="mt-3 text-xs text-bl-muted">
                By subscribing you agree to receive marketing emails. We never share your email and you can unsubscribe at any time.
            </p>
        </form>
    );
}
